import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const ServiceContainer = styled.div`
  padding: var(--spacing-xxl) 0;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-lg);
`;

const PageTitle = styled.h1`
  margin-bottom: var(--spacing-md);
  
  span {
    color: var(--accent-turquoise);
  }
`;

const IntroText = styled.p`
  max-width: 800px;
  margin-bottom: var(--spacing-xl);
  font-size: 1.2rem;
  color: var(--text-light);
`;

const ServiceSection = styled.section`
  margin-bottom: var(--spacing-xxl);
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-xl);
  margin: var(--spacing-xl) 0;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Feature = styled.div`
  background: white;
  border-radius: 8px;
  padding: var(--spacing-lg);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const FeatureTitle = styled.h3`
  margin-bottom: var(--spacing-sm);
  color: var(--accent-turquoise);
`;

const ProcessSection = styled.section`
  margin-top: var(--spacing-xl);
  margin-bottom: var(--spacing-xxl);
`;

const ProcessStep = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: var(--spacing-lg);
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const StepNumber = styled.div`
  background-color: var(--accent-lavender);
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: bold;
  margin-right: var(--spacing-md);
  flex-shrink: 0;
`;

const StepContent = styled.div`
  flex: 1;
`;

const StepTitle = styled.h3`
  margin-bottom: var(--spacing-xs);
  color: var(--accent-turquoise);
`;

const CTASection = styled.div`
  background-color: var(--secondary);
  padding: var(--spacing-xl);
  border-radius: 8px;
  text-align: center;
  margin-top: var(--spacing-xxl);
`;

const CTATitle = styled.h2`
  margin-bottom: var(--spacing-md);
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background-color: var(--accent-turquoise);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  border: 2px solid var(--accent-turquoise);
  transition: all 0.3s ease;
  margin-top: var(--spacing-md);
  
  &:hover {
    background-color: transparent;
    color: var(--text);
  }
`;

const TradeShowSupport = () => {
  return (
    <ServiceContainer>
      <Container>
        <PageTitle>Trade Show <span>Support</span></PageTitle>
        <IntroText>
          Maximize your travel trade show ROI with our comprehensive support services. We handle everything from pre-show 
          marketing to post-show lead nurturing, ensuring you capitalize on every opportunity to grow your business.
        </IntroText>
        
        <ServiceSection>
          <h2>Comprehensive Trade Show Solutions</h2>
          <p>
            Trade shows are a significant investment for travel businesses. Our specialized services ensure you extract maximum 
            value from each event through strategic planning, professional materials, and effective follow-up systems.
          </p>
          
          <FeatureGrid>
            <Feature>
              <FeatureTitle>Marketing Collateral Design</FeatureTitle>
              <p>Eye-catching brochures, flyers, itineraries, and digital presentations tailored to your target audience and destination offerings.</p>
            </Feature>
            <Feature>
              <FeatureTitle>Booth & Display Design</FeatureTitle>
              <p>Attention-grabbing booth layouts, signage, and displays that communicate your brand story and attract qualified leads.</p>
            </Feature>
            <Feature>
              <FeatureTitle>Contest & Giveaway Strategy</FeatureTitle>
              <p>Creative and effective lead generation tactics through contests, giveaways, and interactive experiences that stand out from competitors.</p>
            </Feature>
            <Feature>
              <FeatureTitle>End-to-End Lead Management</FeatureTitle>
              <p>Complete lead capture, CRM setup, follow-up sequences, and performance tracking to convert trade show connections into bookings.</p>
            </Feature>
            <Feature>
              <FeatureTitle>Social Media Integration</FeatureTitle>
              <p>Pre, during, and post-show social media content and campaigns that extend your reach beyond the physical event.</p>
            </Feature>
            <Feature>
              <FeatureTitle>Market Expansion Strategy</FeatureTitle>
              <p>Targeted approaches for entering new markets, identifying key partnerships, and adapting your offerings to regional preferences.</p>
            </Feature>
          </FeatureGrid>
        </ServiceSection>
        
        <ProcessSection>
          <h2>Our Trade Show Support Process</h2>
          <p>We take a strategic approach to ensure your trade show presence delivers measurable results:</p>
          
          <ProcessStep>
            <StepNumber>1</StepNumber>
            <StepContent>
              <StepTitle>Pre-Show Planning & Preparation</StepTitle>
              <p>We develop your trade show strategy, design all necessary marketing materials, create lead capture systems, and prepare your team with key messaging and goals.</p>
            </StepContent>
          </ProcessStep>
          
          <ProcessStep>
            <StepNumber>2</StepNumber>
            <StepContent>
              <StepTitle>Show Execution Support</StepTitle>
              <p>Optional on-site support, daily social media content, real-time lead management, and competitor analysis to maximize your presence during the event.</p>
            </StepContent>
          </ProcessStep>
          
          <ProcessStep>
            <StepNumber>3</StepNumber>
            <StepContent>
              <StepTitle>Post-Show Follow-Up System</StepTitle>
              <p>Implementation of automated and personalized follow-up sequences, lead scoring, itinerary creation templates, and CRM management to convert leads.</p>
            </StepContent>
          </ProcessStep>
          
          <ProcessStep>
            <StepNumber>4</StepNumber>
            <StepContent>
              <StepTitle>Performance Analysis & Optimization</StepTitle>
              <p>Detailed reporting on lead quality, conversion rates, ROI analysis, and strategic recommendations for future trade show investments.</p>
            </StepContent>
          </ProcessStep>
        </ProcessSection>
        
        <CTASection>
          <CTATitle>Ready to Transform Your Trade Show Results?</CTATitle>
          <p>Let's discuss how our specialized trade show support can help your travel business generate more quality leads and bookings from industry events.</p>
          <CTAButton to="/contact">Request Trade Show Support</CTAButton>
        </CTASection>
      </Container>
    </ServiceContainer>
  );
};

export default TradeShowSupport; 